import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { useState } from 'react'

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().regex(/^(\+91|91)?[6-9]\d{9}$/, 'Please enter a valid Indian phone number'),
  company: z.string().min(2, 'Company name must be at least 2 characters'),
  projectType: z.enum(['security', 'development', 'consulting', 'other']),
  budgetRange: z.string().min(1, 'Please select a budget range'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
  honeypot: z.string().max(0, 'Invalid submission')
})

type ContactFormData = z.infer<typeof contactSchema>

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema)
  })

  const onSubmit = async (data: ContactFormData) => {
    if (data.honeypot) return // Honeypot protection
    
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Formspree endpoint for contact@devsecroot.com
      const response = await fetch('https://formspree.io/f/contact@devsecroot.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company,
          projectType: data.projectType,
          budgetRange: data.budgetRange,
          message: data.message
        })
      })

      if (response.ok) {
        setSubmitStatus('success')
        reset()
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Contact Us - DevSecRoot</title>
        <meta name="description" content="Get in touch with DevSecRoot for security assessments, development services, or consulting. We're here to help secure your applications." />
        <link rel="canonical" href="https://devsecroot.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Let's Build Something
              <br />
              <span className="text-gradient">Secure Together</span>
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Ready to secure your applications or build something amazing? 
              We'd love to hear about your project.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:order-2"
            >
              <div className="glass-card-hover p-8">
                <h2 className="text-2xl font-bold text-dsr-text mb-6">
                  Send us a message
                </h2>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center space-x-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400">
                      Thanks! We'll reply within 1 business day (IST).
                    </span>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center space-x-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400">
                      Something went wrong. Please try again or email us directly.
                    </span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Honeypot field */}
                  <input
                    type="text"
                    {...register('honeypot')}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-dsr-text mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        {...register('name')}
                        className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text placeholder-dsr-text/50 focus:border-dsr-accent focus:outline-none transition-colors"
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-dsr-text mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        {...register('email')}
                        className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text placeholder-dsr-text/50 focus:border-dsr-accent focus:outline-none transition-colors"
                        placeholder="john@company.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-dsr-text mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone')}
                      className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text placeholder-dsr-text/50 focus:border-dsr-accent focus:outline-none transition-colors"
                      placeholder="+91 98765 43210"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
                    )}
                    <p className="mt-1 text-xs text-dsr-text/60">All times are IST</p>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-dsr-text mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      {...register('company')}
                      className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text placeholder-dsr-text/50 focus:border-dsr-accent focus:outline-none transition-colors"
                      placeholder="Your Company Inc."
                    />
                    {errors.company && (
                      <p className="mt-1 text-sm text-red-400">{errors.company.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium text-dsr-text mb-2">
                        Project Type *
                      </label>
                      <select
                        id="projectType"
                        {...register('projectType')}
                        className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text focus:border-dsr-accent focus:outline-none transition-colors"
                      >
                        <option value="">Select project type</option>
                        <option value="security">Security Assessment</option>
                        <option value="development">Development Services</option>
                        <option value="consulting">Security Consulting</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.projectType && (
                        <p className="mt-1 text-sm text-red-400">{errors.projectType.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="budgetRange" className="block text-sm font-medium text-dsr-text mb-2">
                        Budget Range *
                      </label>
                      <select
                        id="budgetRange"
                        {...register('budgetRange')}
                        className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text focus:border-dsr-accent focus:outline-none transition-colors"
                      >
                        <option value="">Select budget range</option>
                        <option value="under-25k">Under $25,000</option>
                        <option value="25k-50k">$25,000 - $50,000</option>
                        <option value="50k-100k">$50,000 - $100,000</option>
                        <option value="100k-250k">$100,000 - $250,000</option>
                        <option value="over-250k">Over $250,000</option>
                      </select>
                      {errors.budgetRange && (
                        <p className="mt-1 text-sm text-red-400">{errors.budgetRange.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-dsr-text mb-2">
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      {...register('message')}
                      rows={5}
                      className="w-full px-4 py-3 bg-dsr-card border border-white/10 rounded-lg text-dsr-text placeholder-dsr-text/50 focus:border-dsr-accent focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-400">{errors.message.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:order-1"
            >
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-dsr-text mb-6">
                    Get in touch
                  </h2>
                  <p className="text-lg text-dsr-text/70 mb-8">
                    We're here to help you build secure, scalable applications. 
                    Whether you need a security assessment, development services, 
                    or just want to discuss your project, we'd love to hear from you.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-dsr-text mb-1">Email</h3>
                      <p className="text-dsr-text/70">contact@devsecroot.com</p>
                      <p className="text-sm text-dsr-text/60">We'll respond within 1 business day (IST)</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-dsr-text mb-1">Phone</h3>
                      <p className="text-dsr-text/70">0731-21637642</p>
                      <p className="text-sm text-dsr-text/60">Mon-Fri, 9:30AM-6:30PM IST</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-dsr-text mb-1">Office</h3>
                      <p className="text-dsr-text/70">Indore M.P, India</p>
                      <p className="text-sm text-dsr-text/60">Available for on-site visits</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-dsr-text mb-1">Response Time</h3>
                      <p className="text-dsr-text/70">Within 24 hours</p>
                      <p className="text-sm text-dsr-text/60">For urgent security issues, call us directly</p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-semibold text-dsr-text mb-3">
                    What happens next?
                  </h3>
                  <ol className="space-y-2 text-dsr-text/70">
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-dsr-accent text-black rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                      <span>We'll review your requirements and schedule a consultation call</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-dsr-accent text-black rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                      <span>Discuss your project scope, timeline, and budget</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-dsr-accent text-black rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                      <span>Receive a detailed proposal and project plan</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-dsr-accent text-black rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                      <span>Begin your secure development journey</span>
                    </li>
                  </ol>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ContactPage
